// Billing state for one account, derived from what WE have recorded on the
// accountsInfo doc. Never calls Stripe.
//
// There is no Stripe webhook in this system: Firestore holds what was written
// when the subscription was set up (createSubscription.js) plus an admin's
// record of a cancellation performed in the Stripe dashboard
// (/admin/setSubscriptionCanceled). The per-account Billing modal remains the
// authoritative live view — it discovers real Stripe state on demand. This
// derivation is what the Accounts table shows, where a per-row Stripe call
// would be far too expensive.
//
// Absence of everything means "no subscription", which is the correct reading
// for accounts that were never set up (including Invited accounts).

import { toMs } from './usageTiers';

export const BILLING_STATES = {
  none: {
    label: 'None',
    description: 'No subscription has been set up',
    badgeClass: 'bg-transparent text-muted-foreground border border-gray-300 hover:bg-transparent',
  },
  scheduled: {
    label: 'Scheduled',
    description: 'Set up, first payment still in the future',
    badgeClass: 'bg-blue-500 text-white hover:bg-blue-500',
  },
  started: {
    label: 'Started',
    description: 'Set up and billing has begun',
    badgeClass: 'bg-green-600 text-white hover:bg-green-600',
  },
  cancelled: {
    label: 'Cancelled',
    description: 'Recorded as cancelled by an admin',
    badgeClass: 'bg-gray-500 text-white hover:bg-gray-500',
  },
};

export const BILLING_STATE_KEYS = ['none', 'scheduled', 'started', 'cancelled'];

// Sort weight — "most going on" first when sorting descending.
const STATE_ORDER = { none: 0, cancelled: 1, scheduled: 2, started: 3 };

export function billingStateOrder(state) {
  return STATE_ORDER[state] ?? 0;
}

/**
 * Derive the billing record for one account.
 *
 * account — accountsInfo doc data
 * nowMs   — injectable clock for tests (defaults to Date.now())
 *
 * State precedence, first match wins:
 *   cancelled — an admin recorded a cancellation
 *   started   — the first payment date has passed
 *   scheduled — set up, first payment still ahead
 *   none      — nothing recorded
 */
export function deriveBillingRecord(account, nowMs = Date.now()) {
  const a = account || {};

  const setUpAtMs = toMs(a.subscriptionActivatedAt);
  const startMs = toMs(a.subscriptionStartDate);
  const canceledAtMs = toMs(a.subscriptionCanceledAt);

  // "Set up" is the activation stamp, with the Stripe ids as a fallback for a
  // subscription whose record predates those fields being written.
  const setUp = setUpAtMs != null
    || !!a.subscriptionStripeScheduleId
    || !!a.subscriptionStripeSubscriptionId;

  const started = startMs != null && startMs <= nowMs;

  let state;
  if (canceledAtMs != null) state = 'cancelled';
  else if (setUp && started) state = 'started';
  else if (setUp && startMs != null) state = 'scheduled';
  else if (setUp) state = 'scheduled'; // set up, start date unknown — not "none"
  else state = 'none';

  const discountPercent = a.subscriptionActivatedDiscountPercent != null
    ? Number(a.subscriptionActivatedDiscountPercent)
    : null;
  const monthlyMinorRaw = a.subscriptionActivatedMonthlyMinor;
  const monthlyMinor = monthlyMinorRaw != null && isFinite(Number(monthlyMinorRaw))
    ? Number(monthlyMinorRaw)
    : null;

  return {
    state,
    setUp,
    setUpAtMs,
    setUpByEmail: a.subscriptionActivatedByEmail || null,
    startMs,
    started,
    tier: a.subscriptionActivatedTier || null,
    discountPercent,
    monthlyMinor,
    currency: (a.subscriptionCurrency || a.prepaymentCurrency || '').toLowerCase() || null,
    cancelRequested: !!a.subscriptionCancelRequested,
    cancelRequestedAtMs: toMs(a.subscriptionCancelRequestedAt),
    canceledAtMs,
    canceledRecordedAtMs: toMs(a.subscriptionCanceledRecordedAt),
    canceledByEmail: a.subscriptionCanceledByEmail || null,
    canceledReason: a.subscriptionCanceledReason || null,
  };
}

// Admin views mix currencies, so cad is 'CA$' to tell it apart from usd.
const CURRENCY_SYMBOL = { gbp: '£', usd: '$', eur: '€', cad: 'CA$' };

// Minor units (pence/cents) -> display string. Mirrors BillingModal's fmtMoney.
export function formatMoneyMinor(minor, currency) {
  if (minor === null || minor === undefined || !isFinite(Number(minor))) return '—';
  const cur = (currency || '').toLowerCase();
  const sym = CURRENCY_SYMBOL[cur] || '';
  const amount = (Number(minor) / 100).toFixed(2);
  return sym ? `${sym}${amount}` : `${amount} ${(currency || '').toUpperCase()}`.trim();
}

// Epoch ms -> "3 Jul 2026". Returns an em-dash for missing values so empty
// billing cells read as "nothing to show" rather than looking broken.
export function formatDateMs(ms) {
  if (ms == null) return '—';
  try {
    return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return String(ms);
  }
}
